import { FirebaseData } from "../General/FirebaseData";
import { ChatData } from "../Chat/ChatData";
import { Utilities } from "../../../utils/utilities";
import { PropertyData } from "../Property/PropertyData";
import { LocalData } from "../General/LocalData";
import { FirebaseFile } from "../General/FirebaseFile";
import { MemberData } from "../Property/MemberData";
import { ContractorGroupData } from "../ContractorGroup/ContractorGroupData";
import { CompanyData } from "../Company/CompanyData";
import CollectionLoader from "../General/CollectionLoader";
import { UserDeviceData } from "./UserDeviceData";
import { UserNotifications } from "./Notifications/UserNotifications";
import { NotificationData } from "./Notifications/NotificationData";
import { DataCreator } from "../../../utils/dataCreator";
import i18next from "i18next";
import { getNormalizedLanguageCode } from "../../../libs/i18next/language";
import { AuthError, getAuth, updateEmail, updatePassword } from "firebase/auth";
import { DocumentReference } from "firebase/firestore";

export class UserData extends FirebaseData {
  name: string = "";
  email: string = "";
  phone: string = "";
  userColors: number[];
  companies: CompanyData[] = [];
  properties: PropertyData[] = [];
  chats: ChatData[] = [];
  disabledEmailNotifications: Map<string, boolean> = new Map();
  disabledPushNotifications: Map<string, boolean> = new Map();
  profileImage?: FirebaseFile;
  profileImage_Small?: FirebaseFile;
  memberDatas: MemberData[] = [];
  contractorGroups: ContractorGroupData[] = [];
  lastAcceptedTerms?: number;
  isExternal: boolean = false;
  hasAuth: boolean = false;
  lastLogin?: Date;
  isBlocked: boolean = false;
  favoriteProperties: string[] = [];

  signinProviders: string[] = [];
  devices?: CollectionLoader<UserDeviceData>;
  notifications?: UserNotifications;

  constructor(id: string, collection: string) {
    super(id, collection, undefined);
    this.userColors = Utilities.generateRGBArray();
  }

  setup(data: any) {
    if (data) {
      if (typeof data.name === "string") {
        this.name = data.name;
      }
      if (typeof data.email === "string") {
        this.email = data.email;
      }
      if (typeof data.phone === "string") {
        this.phone = data.phone;
      }
      this.hasAuth = typeof data.hasAuth === "boolean" ? data.hasAuth : false;
      this.isBlocked = typeof data.isBlocked === "boolean" ? data.isBlocked : false;

      if (Array.isArray(data.providerData)) {
        this.signinProviders = [];
        data.providerData.forEach((provider: any) => {
          if (this.isValidString(provider.providerId)) {
            this.signinProviders.push(provider.providerId);
          }
        });
      }
      if (super.isValidObject(data.profileImages)) {
        if (this.isValidObject(data.profileImages.small)) {
          this.profileImage_Small = new FirebaseFile(data.profileImages.small.path);
          this.profileImage_Small.title = data.profileImages.small.name;
          this.profileImage_Small.fileType = data.profileImages.small.type;
        }
        if (this.isValidObject(data.profileImages.large)) {
          this.profileImage = new FirebaseFile(data.profileImages.large.path);
          this.profileImage.title = data.profileImages.large.name;
          this.profileImage.fileType = data.profileImages.large.type;
        }
      }
      // Properties
      if (Array.isArray(data.favoriteProperties)) {
        this.favoriteProperties = [];
        (data.favoriteProperties as unknown[]).forEach((propertyId) => {
          if (typeof propertyId === "string") {
            this.favoriteProperties.push(propertyId);
          }
        });
      }

      if (Array.isArray(data.properties)) {
        this.properties = [];
        (data.properties as any[]).forEach((propertyRef) => {
          let id;

          if (propertyRef instanceof DocumentReference) {
            id = propertyRef.id;
          } else if (this.isValidString(propertyRef)) {
            id = propertyRef;
          }

          let property: PropertyData = LocalData.properties.addData(id);
          this.properties.push(property);
        });
      }

      if (Array.isArray(data.contractorGroups)) {
        this.contractorGroups = [];
        (data.contractorGroups as any[]).forEach((group) => {
          if (this.isValidString(group)) {
            this.addContractorGroupWithId(group);
          }
        });
      }

      if (Array.isArray(data.companies)) {
        this.companies = [];
        (data.companies as any[]).forEach((company) => {
          if (this.isValidString(company)) {
            this.companies.push(LocalData.companies.addData(company));
          }
        });
      }
      if (this.id === LocalData.currentUser.id) {
        if (data.disabledEmailNotifications as object) {
          this.disabledEmailNotifications = new Map(Object.entries(data.disabledEmailNotifications));
        }
        if (data.disabledPushNotifications as object) {
          this.disabledPushNotifications = new Map(Object.entries(data.disabledPushNotifications));
        }
        if (this.isValidNumber(data.lastAcceptedTerms)) {
          this.lastAcceptedTerms = data.lastAcceptedTerms;
        }
        if (Array.isArray(data.chats)) {
          (data.chats as any[]).forEach((chatID) => {
            if (typeof chatID === "string") {
              this.getChatWithId(chatID);
            }
          });
        }
        if (!this.devices) {
          this.devices = new CollectionLoader<UserDeviceData>("devices", this, UserDeviceData);
        }
        if (!this.notifications) {
          this.notifications = new UserNotifications("notifications", this, NotificationData);
        }
      }

      this.lastLogin = Utilities.getDateFromTimestamp(data.lastLogin);

      super.setup(data);
    }
  }

  addContractorGroupWithId(id: string) {
    this.contractorGroups.forEach((group) => {
      if (group.id === id) {
        return;
      }
    });
    let group = LocalData.contractorGroups.addData(id);
    if (!group.members.addData(this.id).loaded) {
      if (this.id === LocalData.currentUser.id) {
        group.members.loadDocument(this.id, undefined);
      }
      this.contractorGroups.push(group);
    } else {
      this.contractorGroups.push(group);
    }
  }

  updateUserEmail(email: string, completion: (error?: AuthError) => void) {
    const auth = getAuth();
    let currentUser = auth.currentUser;
    if (currentUser) {
      if (currentUser.uid === this.id) {
        updateEmail(currentUser, email)
          .then(() => {
            completion(undefined);
          })
          .catch((error: AuthError) => {
            console.log("Failed to update email. " + error.code + " : " + error.message);
            completion(error);
          });
      } else {
        console.error("Can not update email for a non currentUser");
      }
    }
  }

  updateUserPassword(password: string, completion: (error?: AuthError) => void) {
    const auth = getAuth();
    console.log("Updating password");
    let currentUser = auth.currentUser;
    if (currentUser) {
      if (currentUser.uid === this.id) {
        updatePassword(currentUser, password)
          .then(() => {
            console.log("Updated password");
            completion(undefined);
          })
          .catch((error: AuthError) => {
            console.log("Failed to update password. " + error.code + " : " + error.message);
            completion(error);
          });
      } else {
        console.error("Can not update password for a non currentUser");
      }
    }
  }

  addMemberData(member: MemberData) {
    for (let exisitingMember of this.memberDatas) {
      if (exisitingMember.property.id === member.property.id) {
        return;
      }
    }
    this.memberDatas.push(member);
  }

  getProperties(): PropertyData[] {
    let properties: PropertyData[] = [];
    if (this.contractorGroups.length > 0) {
      this.contractorGroups.forEach((group) => {
        group.properties.forEach((property) => {
          let found = false;
          properties.forEach((existing) => {
            if (existing.id === property.id) {
              found = true;
            }
          });
          if (!found && !property.deleted) {
            if (!properties.includes(property)) properties.push(property);
          }
        });
      });
    }
    if (this.memberDatas.length > 0) {
      this.memberDatas.forEach((member) => {
        if (!properties.includes(member.property) && !member.property.deleted) properties.push(member.property);
      });
    }
    return properties;
  }

  setFavoriteProperty(propertyId: string) {
    const updatedPropertyList = [...this.favoriteProperties];
    const propertyIndex = updatedPropertyList.indexOf(propertyId);

    if (propertyIndex !== -1) {
      updatedPropertyList.splice(propertyIndex, 1);
    } else {
      updatedPropertyList.push(propertyId);
    }

    return this.saveUpdate({ favoriteProperties: updatedPropertyList }).then(() => {
      this.favoriteProperties = updatedPropertyList;
    });
  }

  checkForLandlordRoles() {
    for (let property of this.properties) {
      const member = property.members.getData(this.id);
      if (member?.hasLandlord()) {
        return true;
      }
    }
    return false;
  }

  checkForContractorRoles() {
    if (this.contractorGroups.length > 0) {
      return true;
    }
    return false;
  }

  checkForTenantRoles() {
    for (let property of this.properties) {
      const member = property.members.getData(this.id);
      if (member?.hasTenant()) {
        return true;
      }
    }
    return false;
  }

  getChatWithId(id: string): ChatData {
    for (let i in this.chats) {
      if (this.chats[i].id === id) {
        return this.chats[i];
      }
    }
    let chat = new ChatData(id, "chats", undefined);
    this.chats.push(chat);
    return chat;
  }

  saveCurrentDevice() {
    if (!this.isExternal) {
      let browserData = Utilities.retrieveBrowser();
      let browser = browserData.name + browserData.version;
      let osVersion = window.navigator.platform;
      let deviceId = browser + "_" + osVersion;
      this.saveUpdate({
        preferredLangCode: getNormalizedLanguageCode(i18next.language),
      });
      this.devices
        ?.createDataWithMerge(deviceId, {
          platform: "web",
          osVersion: osVersion,
          browser: browser,
          lastLogin: new Date(),
          createdBy: DataCreator.getCreatedBy(),
        })
        .catch((error) => console.log(error));
    }
  }
}
